import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import DataApi from '../../../api/data/DataApi'
import { EditExceptionDto } from '../../../api/data/types'
import { mapExceptionDto } from '../../../mapping/exceptions'
import { DataSubcodes } from '../../../services/errors/consts'
import { isDataError } from '../../../services/errors/utils'
import { HttpError } from '../../../types/common'
import { Exception, ExceptionList } from '../../../types/exceptions'
import { showErrorToast } from '../../../utils/errors'
import { queryNames } from '../../queries'

const dataApi = DataApi.getInstance()

type MutationParams = {
  data: EditExceptionDto
}

export default function useEditException(uuid: string) {
  const queryClient = useQueryClient()
  const texts = useTranslation().t

  const mutation = useMutation(async (params: MutationParams) => {
    const { data } = await dataApi.editException(uuid, params.data)
    return mapExceptionDto(data.data!)
  }, {
    onError: (error: AxiosError<HttpError>) => {
      if (isDataError(error, [DataSubcodes.PLACE_NOT_FOUND, DataSubcodes.PLACE_REMOVED])) {
        queryClient.resetQueries([queryNames.getLocationList])
        queryClient.resetQueries([queryNames.searchLocations])
      }
      if (isDataError(error, DataSubcodes.EXCEPTION_NOT_FOUND)) {
        queryClient.resetQueries([queryNames.getExceptionList])
        queryClient.removeQueries([queryNames.getExceptionDetails, uuid])
      }
      showErrorToast(error, texts)
    },
    onSuccess: (data: Exception, variables: MutationParams) => {
      const queriesData = queryClient.getQueriesData<ExceptionList>([queryNames.getExceptionList])
      for (const queryData of queriesData) {
        queryClient.setQueryData(queryData[0], (oldData?: ExceptionList) => {
          if (!oldData) {
            return oldData
          }
          const newData = [...oldData.exceptions]
          const index = newData.findIndex(p => p.uuid === uuid)
          newData[index] = data
          return {
            totalCount: oldData.totalCount,
            exceptions: newData
          }
        })
      }
      queryClient.resetQueries([queryNames.getEventList])
      toast.success(texts('successes:exception_updated'))
    }
  })

  return mutation
}
